import type { ModelOut } from "@/api/queries";
import { Chip, StatusDot, type Tone } from "@/components/ui/Chip";
import { cn } from "@/lib/cn";

/**
 * The per-chat model picker (PRD FR-3.3, FR-3.6).
 *
 * Grouped by provider with a local/cloud badge, because "which of these runs on
 * my laptop?" is the question a user actually has when the list mixes a cloud
 * key with a local runner. The provider's health rides along beside it, because
 * FR-3.6 wants the badge *wherever a model is chosen* — finding out that a key
 * expired by watching a question fail is the failure this prevents.
 *
 * Hidden models are absent by construction: the catalog endpoint that feeds
 * this list never returns them.
 */

const STATUS_TONE: Record<string, Tone> = {
  ok: "success",
  no_models: "warning",
  auth_failed: "error",
  unreachable: "error",
  not_configured: "warning",
};

const STATUS_LABEL: Record<string, string> = {
  ok: "reachable",
  no_models: "no models",
  auth_failed: "auth failed",
  unreachable: "unreachable",
  not_configured: "not configured",
};

function windowLabel(tokens: number): string {
  if (tokens >= 1_000_000) return `${Math.round(tokens / 100_000) / 10}M ctx`;
  return `${Math.round(tokens / 1_000)}K ctx`;
}

export function ModelPicker({
  models,
  value,
  onChange,
  disabled,
}: {
  models: ModelOut[];
  value: string;
  onChange: (ref: string) => void;
  disabled?: boolean;
}) {
  const groups = new Map<string, ModelOut[]>();
  for (const model of models) {
    const existing = groups.get(model.provider_name);
    if (existing) existing.push(model);
    else groups.set(model.provider_name, [model]);
  }
  const selected = models.find((model) => model.ref === value);
  const status = selected?.provider_status ?? "";

  if (models.length === 0) {
    return <Chip tone="warning">No models available</Chip>;
  }

  return (
    <div className="flex items-center gap-2">
      <select
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        aria-label="Model"
        className={cn(
          "control-sm rounded-element bg-card text-ink type-support max-w-56 truncate",
          "border-border hover:border-border-emphasized focus:border-accent border px-2",
          "transition-[border-color] motion-fast outline-none disabled:opacity-55",
        )}
      >
        {[...groups].map(([provider, entries]) => (
          <optgroup key={provider} label={provider}>
            {entries.map((model) => (
              <option key={model.ref} value={model.ref}>
                {model.label}
                {model.context_window ? ` · ${windowLabel(model.context_window)}` : ""}
                {model.eval_summary
                  ? ` · measured ${String(model.eval_summary.passed ?? 0)}/${String(model.eval_summary.cases ?? 0)}`
                  : ""}
                {model.eval_summary_multi
                  ? ` · cross-source ${String(model.eval_summary_multi.passed ?? 0)}/${String(model.eval_summary_multi.cases ?? 0)}`
                  : ""}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      {selected ? (
        <span className="flex items-center gap-1.5">
          <Chip tone="neutral">{selected.local ? "local" : "cloud"}</Chip>
          {status && status !== "ok" ? (
            <span
              className="type-caption text-ink-disabled inline-flex items-center gap-1"
              title={`${selected.provider_name}: ${STATUS_LABEL[status] ?? status}`}
            >
              <StatusDot tone={STATUS_TONE[status] ?? "neutral"} />
              {STATUS_LABEL[status] ?? status}
            </span>
          ) : (
            <StatusDot
              tone={STATUS_TONE[status] ?? "neutral"}
              className={status ? undefined : "opacity-50"}
            />
          )}
        </span>
      ) : null}
    </div>
  );
}
